'use client';

import * as React from 'react';
import { sortBy } from 'lodash';
import { License } from '@/types/license';
import { Loading } from '@/components/ui/loading';
import FetchSessionLicenses from '@/lib/frontend/store/actionCreators/FetchSessionLicenses';
import FetchSessionOrders from '@/lib/frontend/store/actionCreators/FetchSessionOrders';
import { useAppSelector, useAppStore } from '@/lib/frontend/store/hooks';
import {
  selectCurrentUser,
  selectOrdersByID,
  selectUserLicenses,
} from '@/lib/frontend/store/helpers';
import { H2 } from '@/components/ui/typography';
import { MessageWithIcon } from '@/components/ui/message';
import { Info } from 'lucide-react';
import Link from 'next/link';
import { MailToSupport } from '../mail-to-support';
import { isNotNullOrUndefined } from '@/lib/shared/utils/typeguards';
import { LicensesTableRow } from './licenses-table-row';
import { cn } from '@/lib/shared/ui';

const HeaderCell = (
  props: React.DetailedHTMLProps<
    React.TdHTMLAttributes<HTMLTableDataCellElement>,
    HTMLTableDataCellElement
  >,
) => <td {...props} className={cn('py-3', props.className)} />;

export const LicensesTable = () => {
  const store = useAppStore();
  const currentUser = useAppSelector(selectCurrentUser);
  const licenses = useAppSelector(selectUserLicenses);
  const ordersByID = useAppSelector(selectOrdersByID);

  const loading = currentUser.licenses != null ? currentUser.licenses.pendingResponse : true;
  const licensesErrorMessage = currentUser.licenses?.errorMessage;

  React.useEffect(() => {
    FetchSessionLicenses(store);
    FetchSessionOrders(store);
  }, []);

  const loadedLicenses: License[] = licenses
    .filter(isNotNullOrUndefined)
    .map((license) => license.value)
    .filter(isNotNullOrUndefined);

  const sortedLicenses = sortBy(loadedLicenses, (l) => l.CreatedAt).reverse();
  if (loading) {
    return (
      <div id="licenses">
        <H2>License Overview</H2>
        <Loading />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5" id="licenses">
      <div className="flex flex-col gap-3">
        <H2>License Overview</H2>
        {!licensesErrorMessage && licenses != null && licenses.length === 0 && (
          <MessageWithIcon
            icon={<Info className="h-4 w-4 text-violet-900" />}
            variant="default-light"
            title={<span className="font-bold">Your account has no licenses yet.</span>}
            description={
              <span>
                Buy or rent one{' '}
                <Link href="/shop/" className="font-bold underline text-violet-900">
                  here.
                </Link>
              </span>
            }
          />
        )}

        {licenses != null && licenses.length > 0 && (
          <MessageWithIcon
            icon={<Info className="h-4 w-4 text-violet-900" />}
            variant="default-light"
            title={<span className="font-bold">QLab 5 is available now!</span>}
            description={
              <span>
                <Link href="/upgrade-to-v5" className="font-bold underline text-violet-900">
                  Upgrade your v4 licenses here
                </Link>
                . Please contact <MailToSupport className="font-bold underline text-violet-900" />{' '}
                to upgrade a site license.
              </span>
            }
          />
        )}
      </div>

      {licensesErrorMessage && <div>Unable to load licenses: {licensesErrorMessage}</div>}

      {!licensesErrorMessage && sortedLicenses.length > 0 && (
        <table className="table-auto">
          <thead className="text-sm font-bold p-2 px-3 border-b-[3px] border-b-accent ">
            <tr>
              <HeaderCell>License</HeaderCell>
              <HeaderCell className="hidden md:table-cell">Activations</HeaderCell>
              <HeaderCell className="hidden md:table-cell">Date</HeaderCell>
              <HeaderCell className="hidden md:table-cell">Order No.</HeaderCell>
              <HeaderCell></HeaderCell>
            </tr>
          </thead>
          <tbody>
            {sortedLicenses.map((license, i, arr) => (
              <LicensesTableRow
                license={license}
                key={license.Serial}
                orderNumber={ordersByID[license.OrderID]?.value?.Number}
                className={i < arr.length - 1 ? 'border-b border-b-background/20' : ''}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
